import { alpha, createTheme, Theme } from '@mui/material/styles';
import {
  blue,
  common,
  coral,
  cyan,
  divider,
  error,
  green,
  grey,
  info,
  navy,
  orange,
  pink,
  primary,
  purple,
  red,
  secondary,
  success,
  teal,
  text,
  warning,
  yellow
} from "./colors";

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    white: true;
    'outlined-white': true,
  }
}

export const theme: Theme = createTheme({
  maxContent: 480,
  mixins: {
    toolbar: {
      minHeight: 56,
    },
    sidebar: {
      width: 240,
    },
  },
  palette: {
    mode: "light",
    highlight: pink[500],
    background: {
      default: common.white,
    },
    divider,
    common,
    primary,
    secondary,
    success,
    error,
    warning,
    info,
    text,
    grey,
    green,
    cyan,
    orange,
    pink,
    purple,
    red,
    yellow,
    navy,
    blue,
    coral,
    teal
  },
  typography: {
    htmlFontSize: 16,
    fontFamily: "Public Sans, sans-serif",
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    body1: {
      fontWeight: 400,
      fontSize: 14,
    },
    h1: {
      fontWeight: 700,
      fontSize: 20,
    },
    h2: {
      fontWeight: 700,
      fontSize: 18,
    },
    h3: {
      fontWeight: 700,
      fontSize: 16,
    },
    h4: {
      fontWeight: 700,
      fontSize: 14,
    },
    h5: {
      fontWeight: 700,
      fontSize: 12
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        a: {
          color: pink[500],
          textDecoration: 'none', // Optional: remove underline
          '&:hover': {
            textDecoration: 'underline',
            color: pink[400],
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.pink[500]
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginTop: theme.spacing(4),
          // Only target standard variant
          '&:has(.MuiInputLabel-standard)': {
            display: 'flex',
            flexWrap: 'wrap',
            position: 'relative',
            flexDirection: 'row',
            alignItems: 'baseline',
            paddingBottom: theme.spacing(3),

            '& .MuiInputLabel-standard': {
              position: 'static',
              transform: 'none',
              marginRight: theme.spacing(1),
              marginBottom: 0,
              flexShrink: 0,
              paddingBottom: '8px',
              zIndex: 1,
              color: theme.palette.text.secondary,
            },

            '& .MuiInput-root': {
              marginTop: 0,
              flex: 1,
              '&:before, &:after': {
                width: 'calc(100% + 60px)',
                left: '-60px',
              }
            },

            '&:before': {
              content: '""',
              position: 'absolute',
              bottom: theme.spacing(3),
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: theme.palette.divider,
              zIndex: 1,
              transition: theme.transitions.create(['border-color', 'background-color', 'transform']),
            },

            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: theme.spacing(3),
              left: 0,
              right: 0,
              height: '2px',
              backgroundColor: theme.palette.primary.main,
              zIndex: 1,
              transform: 'scaleX(0)',
              transition: theme.transitions.create(['transform'], {
                duration: 200,
                easing: 'cubic-bezier(0.0, 0, 0.2, 1)',
              }),
            },

            '&:hover:before': {
              backgroundColor: theme.palette.text.primary,
            },

            '&.Mui-focused:after': {
              transform: 'scaleX(1)',
            },

            '&.Mui-error:after': {
              backgroundColor: theme.palette.error.main,
              transform: 'scaleX(1)',
            },

            '& .MuiFormHelperText-root': {
              width: '100%',
              marginLeft: 0,
              position: 'absolute',
              bottom: 0,
              left: 0,
              paddingTop: theme.spacing(1),
            }
          }
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '0.75rem',
          lineHeight: 1.2,
        }),
      },
    },
    MuiInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:before': {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          '&.Mui-focused:after': {
            borderBottom: `1px solid ${theme.palette.primary.main}`,
          },

          // Remove default underline for standard variant within form control
          '.MuiFormControl-root:has(.MuiInputLabel-standard) &:before, .MuiFormControl-root:has(.MuiInputLabel-standard) &:after': {
            display: 'none',
          },

          '&.MuiInputBase-adornedStart': {
            paddingLeft: 0,
          }
        }),
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontSize: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          if (ownerState.color === 'error') {
            return {
              color: theme.palette.error.main
            }
          }

          return {
            fontWeight: 500,
            // color: theme.palette.text.primary
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
      },
    },
    MuiCardContent: {
      // styleOverrides: {
      //   root: ({ ownerState, theme }) => ({
      //     padding: theme.spacing(2),
      //   }),
      // },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          fontWeight: 500
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          borderRadius: theme.shape.borderRadius * 10
        }),
        sizeLarge: ({ theme }) => ({
          minHeight: theme.spacing(6)
        }),
        sizeMedium: ({ theme }) => ({
          minHeight: theme.spacing(6)
        })
      },
      variants: [
        {
          props: { variant: 'white' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.common.white,
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: theme.palette.grey[100],
            },
          }),
        },
        {
          props: { variant: 'outlined-white' },
          style: ({ theme }) => ({
            backgroundColor: 'transparent',
            color: theme.palette.common.white,
            border: `1px solid ${theme.palette.common.white}`,
            '&:hover': {
              backgroundColor: alpha(theme.palette.common.white, 0.6),
              color: theme.palette.text.primary,
            },
          }),
        }
      ],
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          color: theme.palette.text.secondary,
          minWidth: 'auto',
          padding: '8px 12px',
          '&.Mui-selected': {
            color: theme.palette.highlight,
          }
        }),
        label: ({ theme }) => ({
          fontSize: '0.75rem',
          '&.Mui-selected': {
            fontSize: '0.8rem',
            fontWeight: theme.typography.fontWeightBold,
          }
        }),
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontWeight: 500,
          '& input': {
            textAlign: 'inherit',
          },
        }),
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 36,
          height: 20,
          padding: 0,
          marginRight: 8,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: theme.palette.common.white,
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.pink[500],
                opacity: 1,
                border: 0,
                ...theme.applyStyles('dark', {
                  backgroundColor: theme.palette.pink[400],
                }),
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              color: theme.palette.success.main,
              border: '6px solid',
              BorderColor: theme.palette.common.white,
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
              color: theme.palette.grey[100],
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.7,
            },
          },
          '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 16,
            height: 16,
          },
          '& .MuiSwitch-track': {
            opacity: 1,
            borderRadius: 26 / 2,
            backgroundColor: theme.palette.grey[100],
            transition: theme.transitions.create(['background-color'], {
              duration: 500,
            }),
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiInputLabel-standard': {
            position: 'relative',
            transform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.primary,
            marginBottom: 0,
            marginRight: theme.spacing(2),
            display: 'inline-block',
            marginTop: 0,
            '&.MuiInputLabel-shrink': {
              transform: 'none',
            },
            '&.Mui-focused': {
              color: theme.palette.text.primary,
            },
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          height: '4px',
          backgroundColor: theme.palette.pink[500],
        }),
      },
    },
  },
});
